import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { CAREER_ROLES } from '@/content/careersRoles';

interface CareerApplication {
  id: string;
  roleId: string;
  roleTitle: string;
  fullName: string;
  email: string;
  phone?: string;
  city?: string;
  country?: string;
  cvFileName?: string;
  status: string;
  createdAt?: string;
}

function token() {
  return localStorage.getItem('accessToken') || localStorage.getItem('token');
}

const AdminCareersApplications: React.FC = () => {
  const [rows, setRows] = useState<CareerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set('status', statusFilter);
      if (roleFilter) qs.set('roleId', roleFilter);
      const res = await fetch(`/api/admin/careers/applications?${qs}`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {},
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load');
      setRows(data.data || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, roleFilter]);

  const updateStatus = async (id: string, status: string) => {
    try {
      const res = await fetch(`/api/admin/careers/applications/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
        },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed');
      setRows((prev) => prev.map((r) => (r.id === id ? data.data : r)));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  const openCv = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/careers/applications/${id}/cv-url`, {
        headers: token() ? { Authorization: `Bearer ${token()}` } : {},
      });
      const data = await res.json();
      if (!data.success || !data.data?.url) {
        throw new Error(data.message || 'Could not get CV download link');
      }
      window.open(data.data.url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not open CV');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap spacing={1}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            Career applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Applications from the public Careers page (CVs in shared video S3 bucket under job/).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(String(e.target.value))}>
              <MenuItem value="">All</MenuItem>
              {CAREER_ROLES.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(String(e.target.value))}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="submitted">submitted</MenuItem>
              <MenuItem value="reviewing">reviewing</MenuItem>
              <MenuItem value="interview">interview</MenuItem>
              <MenuItem value="rejected">rejected</MenuItem>
              <MenuItem value="hired">hired</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={() => void load()} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#fff' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>CV</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No applications yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.fullName}</TableCell>
                    <TableCell>{row.roleTitle}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[row.city, row.country].filter(Boolean).join(', ') || row.phone || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                        onClick={() => void openCv(row.id)}
                      >
                        {row.cvFileName || 'Download CV'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.status}
                        onChange={(e) => void updateStatus(row.id, String(e.target.value))}
                        sx={{ minWidth: 120 }}
                      >
                        {['submitted', 'reviewing', 'interview', 'rejected', 'hired', 'withdrawn'].map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminCareersApplications;
